import React from 'react';
import { Thermometer, Calculator, Home, MapPin } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров калькулятора теплопотерь
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  climateData
}) => {
  // Обработчик изменения типа здания
  const handleBuildingTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      buildingType: type,
      // Дополнительные настройки в зависимости от типа здания
      ...(type === 'apartment' ? {
        floors: '1',
        roofType: 'adjacent',
        floorType: 'adjacent'
      } : {
        roofType: 'exposed',
        floorType: 'exposed'
      })
    }));
  };
  
  // Обработчик изменения региона
  const handleRegionChange = (region) => {
    if (formData && climateData && climateData[region]) {
      setFormData(prev => ({
        ...prev,
        region,
        outdoorTemperature: climateData[region].designTemp,
        cityName: climateData[region].name
      }));
    }
  };
  
  // Обработчик изменения температуры
  const handleTemperatureChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Обработчик изменения возраста здания
  const handleBuildingAgeChange = (age) => {
    setFormData(prev => {
      const newState = { ...prev, buildingAge: age };
      
      // Обновление параметров в зависимости от возраста здания
      if (age === 'old') {
        newState.wallMaterial = 'brick_noninsulated';
        newState.windowType = 'single_glazed';
        newState.infiltrationRate = 'high';
        newState.ventilationType = 'natural';
      } else if (age === 'new') {
        newState.wallMaterial = 'brick_insulated';
        newState.windowType = 'double_glazed_modern';
        newState.infiltrationRate = 'low';
      }
      
      return newState;
    });
  };
  
  // Функция расчета
  const calculateHeatLoss = () => {
    // Вызываем кнопку расчета из основной формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Популярные регионы России для быстрого выбора
  const popularRegions = [
    { id: 'moscow', name: 'Москва' },
    { id: 'saint_petersburg', name: 'Санкт-Петербург' },
    { id: 'novosibirsk', name: 'Новосибирск' },
    { id: 'yekaterinburg', name: 'Екатеринбург' }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-red-500" />
        Быстрая настройка расчета
      </h3>
      
      <div className="space-y-5">
        {/* Тип здания */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип здания
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleBuildingTypeChange('residential')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.buildingType === 'residential' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Квартира</div>
              <div className="text-xs opacity-75">В доме</div>
            </button>
            <button
              type="button"
              onClick={() => handleBuildingTypeChange('commercial')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.buildingType === 'commercial' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Коммерч.</div>
              <div className="text-xs opacity-75">Офис/Магазин</div>
            </button>
          </div>
        </div>
        
        {/* Регион */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-700 dark:text-gray-300 font-medium">Регион России</label>
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              {climateData && formData && climateData[formData.region]?.name || 'Выберите регион'}
            </span>
          </div>
          
          {/* Популярные регионы */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            {popularRegions.map(region => (
              <button
                key={region.id}
                type="button"
                onClick={() => handleRegionChange(region.id)}
                className={`px-2 py-1.5 text-xs rounded border transition-colors flex items-center justify-center
                  ${formData.region === region.id 
                    ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <MapPin className="w-3 h-3 mr-1" />
                {region.name}
              </button>
            ))}
          </div>
          
          {/* Полный список регионов */}
          <select
            className="form-select w-full"
            value={formData.region}
            onChange={(e) => handleRegionChange(e.target.value)}
          >
            {climateData && Object.keys(climateData).map(regionId => (
              <option key={regionId} value={regionId}>
                {climateData[regionId].name} ({climateData[regionId].designTemp}°C)
              </option>
            ))}
          </select>
        </div>
        
        {/* Температура */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="outdoorTemperature" className="text-gray-700 dark:text-gray-300 font-medium">
              Расчетная температура наружного воздуха
            </label>
            <span className="text-red-600 dark:text-red-400 font-medium">{formData.outdoorTemperature}°C</span>
          </div>
          <input
            type="range"
            id="outdoorTemperature"
            name="outdoorTemperature"
            min="-50"
            max="10"
            step="1"
            value={formData.outdoorTemperature}
            onChange={handleTemperatureChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>-50°C</span>
            <span>-25°C</span>
            <span>0°C</span>
            <span>+10°C</span>
          </div>
        </div>
        
        {/* Температура внутри */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="indoorTemperature" className="text-gray-700 dark:text-gray-300 font-medium">
              Температура внутри помещения
            </label>
            <span className="text-red-600 dark:text-red-400 font-medium">{formData.indoorTemperature}°C</span>
          </div>
          <input
            type="range"
            id="indoorTemperature"
            name="indoorTemperature"
            min="16"
            max="24"
            step="1"
            value={formData.indoorTemperature}
            onChange={handleTemperatureChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>+16°C</span>
            <span>+20°C</span>
            <span>+24°C</span>
          </div>
        </div>
        
        {/* Возраст здания */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Возраст здания
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleBuildingAgeChange('new')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.buildingAge === 'new' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Новое</div>
              <div className="text-xs opacity-75">После 2000 г.</div>
            </button>
            <button
              type="button"
              onClick={() => handleBuildingAgeChange('old')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.buildingAge === 'old' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Старое</div>
              <div className="text-xs opacity-75">До 2000 г.</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateHeatLoss}
          icon={<Thermometer className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать теплопотери
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;